import React, { useRef } from "react";
import { CircularProgress, Snackbar, Alert } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import { CustomInput, CustomTextArea } from "../../components/Common/CustomInputs";
import { CustomDropdown } from "../../components/Common/CustomDropdown";
import { CustomBtn } from "../../components/Common/CustomBtn";
import { getInitials } from "../../components/ui/Gapper";
import { colors } from "../../theme/theme";
import { GENDER_OPTIONS } from "../Content/MyProfile/MyProfile.constants";
import { useClientProfile } from "./ClientProfile.hooks";
import {
  SectionCard,
  SectionTitle,
  FieldGrid,
  FullRow,
  AvatarRow,
  ProfileAvatar,
  AvatarName,
  AvatarSubtitle,
  AvatarWrap,
  AvatarEditButton,
  InputFieldLabel,
  SaveBar,
  LoadingBox,
} from "../Content/MyProfile/MyProfile.styles";

const ClientProfile = () => {
  const {
    form,
    loading,
    saving,
    uploading,
    setField,
    handleUpload,
    handleSubmit,
    snack,
    closeSnack,
  } = useClientProfile();
  const fileRef = useRef(null);

  if (loading) {
    return (
      <LoadingBox>
        <CircularProgress sx={{ color: colors.secondary }} />
      </LoadingBox>
    );
  }

  const fullName = `${form.firstName} ${form.lastName}`.trim();

  const SaveButton = (
    <CustomBtn
      text={saving ? "Saving..." : "Save Changes"}
      onClick={handleSubmit}
      disabled={saving}
      noMb
      noMt
    />
  );

  return (
    <div>
      <SectionCard>
        <AvatarRow>
          <AvatarWrap>
            <ProfileAvatar src={form.avatar || undefined}>
              {uploading ? (
                <CircularProgress size={20} sx={{ color: colors.white }} />
              ) : (
                getInitials(fullName || "?")
              )}
            </ProfileAvatar>
            <AvatarEditButton
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Upload photo"
            >
              <PhotoCameraIcon />
            </AvatarEditButton>
          </AvatarWrap>
          <div>
            <AvatarName>{fullName || "Your name"}</AvatarName>
            <AvatarSubtitle>{form.email}</AvatarSubtitle>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.target.value = "";
            }}
          />
        </AvatarRow>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          <PersonOutlineIcon fontSize="small" /> Personal Information
        </SectionTitle>
        <FieldGrid>
          <CustomInput
            label="First Name"
            required
            value={form.firstName}
            setValue={(v) => setField("firstName", v)}
            noIcon
          />
          <CustomInput
            label="Last Name"
            required
            value={form.lastName}
            setValue={(v) => setField("lastName", v)}
            noIcon
          />
          {/* Email is read-only — changing it needs a verified flow. */}
          <CustomInput label="Email" value={form.email} setValue={() => {}} noIcon />
          <CustomInput
            label="Phone"
            value={form.phone}
            setValue={(v) => setField("phone", v)}
            noIcon
          />
          <div>
            <InputFieldLabel>Date of Birth</InputFieldLabel>
            <CustomInput
              type="date"
              value={form.dob}
              setValue={(v) => setField("dob", v)}
              noIcon
            />
          </div>
          <CustomInput
            label="State"
            value={form.state}
            setValue={(v) => setField("state", v)}
            noIcon
          />
        </FieldGrid>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          <FavoriteBorderIcon fontSize="small" /> Care Preferences
        </SectionTitle>
        <FieldGrid>
          <CustomDropdown
            label="Gender"
            value={form.gender}
            setValue={(v) => setField("gender", v)}
            data={GENDER_OPTIONS}
            placeholder="Select"
            noIcon
          />
          <CustomInput
            label="Practitioner Gender Preference"
            value={form.genderPreference}
            setValue={(v) => setField("genderPreference", v)}
            noIcon
          />
          <CustomInput
            label="Preferred Language"
            value={form.language}
            setValue={(v) => setField("language", v)}
            noIcon
          />
          <CustomInput
            label="Session Type"
            value={form.sessionType}
            setValue={(v) => setField("sessionType", v)}
            noIcon
          />
          <FullRow>
            <CustomTextArea
              label="Personal Goals"
              value={form.personalGoals}
              setValue={(v) => setField("personalGoals", v)}
              rows={4}
              noIcon
            />
          </FullRow>
        </FieldGrid>
      </SectionCard>

      <SaveBar>{SaveButton}</SaveBar>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ClientProfile;
